import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { QUALTRICS_DATA_CENTERS } from '@modules/integrationsV2/constants';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { encodeOAuthState } from '@modules/integrationsV2/utils/encodeOAuthState';
import { LoadingButton } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

const getQualtricsBaseURL = (dataCenter: string) =>
  `https://${dataCenter}.qualtrics.com/oauth2/auth`;

type QualtricsFormData = {
  clientID: string;
  clientSecret: string;
  dataCenter: string;
};

const initialState: QualtricsFormData = {
  clientID: '',
  clientSecret: '',
  dataCenter: '',
};

interface QualtricsFormModalProps {
  integrationType: IntegrationType;
}

export const QualtricsFormModal = ({
  integrationType,
}: QualtricsFormModalProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const [formData, setFormData] = useState<QualtricsFormData>(initialState);

  const handleOnChange = <T extends { name: string; value: string }>(
    eventTaget: T,
  ) => {
    const { name, value } = eventTaget;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = () => {
    const queryParams = {
      response_type: 'code',
      scope: 'read:survey_responses read:surveys',
      redirect_uri: `${window.location.origin}/settings/integrations`,
      client_id: formData.clientID,
      state: encodeOAuthState({
        channelType: IntegrationChannelType.Qualtrics,
        direction: IntegrationDirection.Inbound,
        data: {
          tenantSlug,
          clientID: formData.clientID,
          clientSecret: formData.clientSecret,
          dataCenter: formData.dataCenter,
        },
        integrationType,
      }),
    };

    const redirectURL = new URL(getQualtricsBaseURL(formData.dataCenter));
    redirectURL.search = new URLSearchParams(queryParams).toString();

    const windowURL = window.open(redirectURL, '_self');

    if (windowURL) {
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Qualtrics,
      });
    }
  };

  const isConnectButtonDisabled =
    !formData.clientID || !formData.clientSecret || !formData.dataCenter;

  return (
    <>
      <ModalHeader title="Qualtrics" showCloseButton={true} />
      <DialogContent dividers>
        <Stack pt={1} spacing={2}>
          <TextField
            required
            label="Qualtrics Client ID"
            placeholder="Enter Client ID"
            onChange={(event) => handleOnChange(event.target)}
            name="clientID"
            value={formData.clientID}
          />
          <TextField
            required
            label="Qualtrics Client Secret"
            placeholder="Enter Cient Secret"
            onChange={(event) => handleOnChange(event.target)}
            name="clientSecret"
            value={formData.clientSecret}
          />
          <FormControl required>
            <InputLabel id="data-center-select-label">
              Qualtrics Data Center
            </InputLabel>
            <Select
              name="dataCenter"
              labelId="data-center-select-label"
              label="Qualtrics Data Center"
              onChange={(event) => handleOnChange(event.target)}
              value={formData.dataCenter}
            >
              {QUALTRICS_DATA_CENTERS.map((dataCenter) => {
                return (
                  <MenuItem key={dataCenter.value} value={dataCenter.value}>
                    {dataCenter.name} - {dataCenter.value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={isConnectButtonDisabled}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </>
  );
};
